import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { EmailAuthForm } from './EmailAuthForm';
import { useSessionStore, selectIsAuthenticated } from '../../style-transfer/state/sessionStore';
import {
  useAuthStore,
  selectUserEmail,
  selectAuthDraftMode
} from '../state/authStore';
import { VoucherRedeemForm } from './VoucherRedeemForm';
import { showToast } from '../../../shared/notifications/toastStore';

interface MemberLoginOverlayProps {
  onClose: () => void;
}

export function MemberLoginOverlay({ onClose }: MemberLoginOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const successCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  const { t } = useTranslation();
  const isAuthenticated = useSessionStore(selectIsAuthenticated);
  const userEmail = useAuthStore(selectUserEmail);
  const mode = useAuthStore(selectAuthDraftMode);
  const setDraftMode = useAuthStore((state) => state.setDraftMode);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    contentRef.current?.focus();
    return () => {
      if (successCloseTimerRef.current) {
        clearTimeout(successCloseTimerRef.current);
        successCloseTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setDraftMode('login');
    }
  }, [isAuthenticated, setDraftMode]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === overlayRef.current) {
      onClose();
    }
  };

  const contentClasses = clsx(
    'relative w-full max-w-xl overflow-hidden rounded-3xl border border-ai-surface-glow/40 bg-ai-surface-elevated/90 shadow-ai-card',
    'focus:outline-none focus-visible:ring focus-visible:ring-ai-focus'
  );

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-md px-4 py-6 sm:px-6 sm:py-10"
      onMouseDown={handleBackdropClick}
    >
      <div
        ref={contentRef}
        className={contentClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-ai-surface-glow/30 text-sm text-ai-text-secondary transition-colors hover:border-ai-primary/60 hover:text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:right-5 sm:top-5 sm:h-10 sm:w-10"
          aria-label={t('app.actions.close')}
          onClick={onClose}
        >
          X
        </button>

        <div className="max-h-[calc(100vh-3rem)] overflow-y-auto p-6 sm:max-h-[calc(100vh-4rem)] sm:p-8">
          <div className="space-y-4 sm:space-y-6">
            <div className="space-y-1.5 sm:space-y-2">
              <h2 id={titleId} className="font-display text-[1.75rem] font-semibold text-ai-text-primary sm:text-2xl">
                {isAuthenticated
                  ? t('loginOverlay.heading.authenticated')
                  : t(`loginOverlay.heading.${mode}`)}
                {isAuthenticated && userEmail
                  ? t('memberPage.auth.signedInAs', { email: userEmail })
                  : t(`loginOverlay.description.${mode}`)}
              </h2>
            </div>

            {isAuthenticated ? (
              <div className="rounded-2xl border border-ai-surface-glow/30 bg-ai-surface-elevated/60 p-4 sm:p-5">
                <VoucherRedeemForm
                  variant="authenticated"
                  onSuccess={() => {
                    showToast({
                      message: t('voucherActivation.toast.topUpSuccess'),
                      tone: 'success'
                    });
                    if (successCloseTimerRef.current) {
                      clearTimeout(successCloseTimerRef.current);
                    }
                    successCloseTimerRef.current = setTimeout(() => {
                      successCloseTimerRef.current = null;
                      onClose();
                    }, 1500);
                  }}
                />
              </div>
            ) : (
              <div className="space-y-5 sm:space-y-6">
                {mode === 'voucher' ? (
                  <VoucherRedeemForm onModeChange={setDraftMode} />
                ) : (
                  <EmailAuthForm mode={mode} onModeChange={setDraftMode} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
